// Country/dial-code table for the "Connect with an advisor" phone field
// (ticket 009). Frontend-only — the edge function does NOT import this; it
// only runs the generic, country-agnostic envelope check in
// supabase/functions/_shared/phoneValidation.ts (D7). A small hand-written
// table, not a phone-number library, curated for this app's actual
// audience (US persons living in Spain), not an exhaustive ~200-country
// list (D3/D6 of ticket 009's plan).
//
// PHONE_ERROR_MESSAGE is imported from phoneValidation.js rather than
// redefined here — one error string for both the per-country check and the
// generic envelope check (rule 4).

import { PHONE_ERROR_MESSAGE } from './phoneValidation'

export const DEFAULT_COUNTRY_CODE = 'US'

// flag: a Unicode regional-indicator emoji, shown before the name in the
// <option> label (John's call — easier to scan a short list visually than
// read every country name). 'Other' gets no flag (there's no single
// country it represents). Renders as a flag on macOS/iOS/most browsers;
// some Windows/Android combinations show the two-letter code instead —
// the name text is always present too, so the option stays legible either way.
export const PHONE_COUNTRIES = [
  { code: 'US',    name: 'United States',  flag: '🇺🇸', dialCode: '+1',   minDigits: 10, maxDigits: 10, placeholder: 'e.g. 2125550134' },
  { code: 'CA',    name: 'Canada',         flag: '🇨🇦', dialCode: '+1',   minDigits: 10, maxDigits: 10, placeholder: 'e.g. 4165550134' },
  { code: 'ES',    name: 'Spain',          flag: '🇪🇸', dialCode: '+34',  minDigits: 9,  maxDigits: 9,  placeholder: 'e.g. 612345678' },
  { code: 'GB',    name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44',  minDigits: 10, maxDigits: 10, placeholder: 'e.g. 7911123456' },
  { code: 'DE',    name: 'Germany',        flag: '🇩🇪', dialCode: '+49',  minDigits: 10, maxDigits: 11, placeholder: 'e.g. 15123456789' },
  { code: 'FR',    name: 'France',         flag: '🇫🇷', dialCode: '+33',  minDigits: 9,  maxDigits: 9,  placeholder: 'e.g. 612345678' },
  { code: 'IT',    name: 'Italy',          flag: '🇮🇹', dialCode: '+39',  minDigits: 9,  maxDigits: 10, placeholder: 'e.g. 3123456789' },
  { code: 'PT',    name: 'Portugal',       flag: '🇵🇹', dialCode: '+351', minDigits: 9,  maxDigits: 9,  placeholder: 'e.g. 912345678' },
  { code: 'MX',    name: 'Mexico',         flag: '🇲🇽', dialCode: '+52',  minDigits: 10, maxDigits: 10, placeholder: 'e.g. 5512345678' },
  { code: 'IE',    name: 'Ireland',        flag: '🇮🇪', dialCode: '+353', minDigits: 9,  maxDigits: 9,  placeholder: 'e.g. 851234567' },
  { code: 'AU',    name: 'Australia',      flag: '🇦🇺', dialCode: '+61',  minDigits: 9,  maxDigits: 9,  placeholder: 'e.g. 412345678' },
  { code: 'OTHER', name: 'Other',          flag: '',   dialCode: '',     minDigits: 4,  maxDigits: 14, placeholder: 'Phone number' },
]

export function getCountry(code) {
  return PHONE_COUNTRIES.find(c => c.code === code) || PHONE_COUNTRIES.find(c => c.code === DEFAULT_COUNTRY_CODE)
}

// digits must already be stripped to digits-only by the caller (D8's live
// stripping in AdvisorConnectForm). Empty digits is always valid — the
// national-number field is optional (D2); the length check only runs when
// something was actually typed.
export function validatePhoneForCountry(countryCode, digits) {
  if (!digits) return { ok: true, error: null }
  const country = getCountry(countryCode)
  if (digits.length < country.minDigits || digits.length > country.maxDigits) {
    return { ok: false, error: PHONE_ERROR_MESSAGE }
  }
  return { ok: true, error: null }
}
